<template>
  <div
    id="assistant-step-3"
    class="assistant-forms">
    <h2 class="title">Human handoff</h2>
    <p class="subtitle">Define how your AI assistant should transfer chats to human agents.</p>

    <JsonSchema
      ref="jsonSchema"
      v-model="value"
      :schema="schema | processSchema"
      :components="componentsConfig"
      :ui-schema="uiSchema"
      :wrapper="wrapperComponentConfig"
      @init-default="initDefault"
      @input="() => {}" />

    <!-- <div> dataModel </div> -->
    <!-- <pre class="debug"> {{ value }} </pre> -->
  </div>
</template>

<script>
// TODO - check this code before publishing Assistants feature

import JsonSchema from '@roma219/vue-jsonschema-form';
import { debounce } from 'debounce';

import SBIconList from '@/helpers/lists/sb-icon-list';
import ToasterMixin from '../../../mixins/Toaster';

const schemaBP = {
  type: 'object',
  properties: {
    condition1: {
      type: 'dropdown',
      title: '🧐 If you cannot provide an answer to the contact',
      validators: 'required',
      options: [
        { value: 1, label: 'Assign chat to the support team' },
        { value: 2, label: 'Collect info and assign chat to the support team' },
      ],
    },
    assign: {
      showIf: {
        input: 'condition1',
        value: [1],
      },
      type: 'object',
      child: true,
      properties: {
        message: {
          type: 'textarea',
          title: 'Send a message to confirm assignment',
          placeholder: 'Hmm, I don\'t have the information you\'re looking for. Would you like me to connect you with our support team for further assistance?',
          value: 'Hmm, I don\'t have the information you\'re looking for. Would you like me to connect you with our support team for further assistance?',
          validators: 'required|minLength:20|maxLength:1024',
          warningValidators: 'minLength',
        },
        buttons: {
          child: true,
          value: [
            { title: '👍 Yes', fixed: true },
            { title: 'Continue chat', fixed: true },
          ],
          type: 'message-buttons',
          addButtonIcon: SBIconList.PLUS,
          addButtonText: 'Add button',
          // buttonMaxTextLength: 25,   // This will prevent adding text longer than 25 characters
          minButtons: 0,
          maxButtons: 2,
          returnKey: 'responseButtons',
          enter: 'Add',
        },
        dividerFull: {
          type: 'dividerFull',
        },
        positiveResponse: {
          type: 'textarea',
          title: 'If the response is positive then send a message',
          value: 'Thanks! Our support team will contact you shortly to assist you further.',
          placeholder: 'Thanks! Our support team will contact you shortly to assist you further.',
          validators: 'required',
          validatorMessages: {
            required: 'Message is required.',
          },
        },
        actionsTest: {
          value: [
            { value: 2 },
          ],
          type: 'action-buttons',
          options: [
            { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
            { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
            { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
          ],
          addActionIcon: SBIconList.PLUS,
          addActionText: 'Add action',
          returnKey: 'responseActions',
        },
        divider: {
          type: 'divider',
        },
        question: {
          type: 'input',
          title: 'If the contact\'s response is negative',
          value: 'Continue chatting',
          validators: 'required',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'AI will keep chatting with the contact.',
        },
      },
    },
    collect: {
      showIf: {
        input: 'condition1',
        value: [2],
      },
      type: 'object',
      child: true,
      properties: {
        message: {
          type: 'textarea',
          title: 'Send a message to confirm assignment',
          placeholder: 'Hmm, I don\'t have the information you\'re looking for. Would you like me to connect you with our support team for further assistance?',
          value: 'Hmm, I don\'t have the information you\'re looking for. Would you like me to connect you with our support team for further assistance?',
          validators: 'required|minLength:20|maxLength:1024',
          warningValidators: 'minLength',
        },
        buttons: {
          child: true,
          value: [
            { title: '👍 Yes', fixed: true },
            { title: 'Continue chat', fixed: true },
          ],
          type: 'message-buttons',
          addButtonIcon: SBIconList.PLUS,
          addButtonText: 'Add button',
          // buttonMaxTextLength: 25,   // This will prevent adding text longer than 25 characters
          minButtons: 0,
          maxButtons: 2,
          returnKey: 'responseButtons',
          enter: 'Add',
        },
        dividerFull: {
          type: 'dividerFull',
        },
        positiveResponse: {
          type: 'textarea',
          title: 'If the response is positive collect info',
          placeholder: 'Full name, email, and issue description.',
          validators: 'required',
          validatorMessages: {
            required: 'Message is required.',
          },
        },
        positiveResponse2: {
          type: 'textarea',
          title: 'When you\'ve collected info send a message',
          value: 'Thanks! Our support team will contact you shortly to assist you further.',
          placeholder: 'Thanks! Our support team will contact you shortly to assist you further.',
          validators: 'required',
          // validatorMessages: {
          //   required: 'Message is required.',
          // },
        },
        actionsTest: {
          value: [
            { value: 2 },
            { value: 3 },
          ],
          type: 'action-buttons',
          options: [
            { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
            { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
            { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
          ],
          addActionIcon: SBIconList.PLUS,
          addActionText: 'Add action',
          returnKey: 'responseActions',
        },
        divider: {
          type: 'divider',
        },
        question: {
          type: 'input',
          title: 'If the contact\'s response is negative',
          value: 'Continue chatting',
          validators: 'required',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'AI will keep chatting with the contact.',
        },
      },
    },
    condition2: {
      type: 'dropdown',
      title: '👥 If the contact requests to speak with the support team',
      validators: 'required',
      options: [
        { value: 1, label: 'Assign chat to the support team' },
        { value: 2, label: 'Collect info and assign chat to the support team' },
      ],
    },
    requestSupport: {
      showIf: {
        input: 'condition2',
        value: [1],
      },
      type: 'object',
      child: true,
      properties: {
        message: {
          type: 'textarea',
          title: 'Send a message to confirm assignment',
          value: 'I\'d be happy to connect you with our support team. Could you please confirm if you\'d like to proceed?',
          placeholder: 'I\'d be happy to connect you with our support team. Could you please confirm if you\'d like to proceed?',
          validators: 'required|maxLength:1024',
        },
        buttons: {
          child: true,
          value: [
            { title: '👍 Yes', fixed: true },
            { title: 'Continue chat', fixed: true },
          ],
          type: 'message-buttons',
          addButtonIcon: SBIconList.PLUS,
          addButtonText: 'Add button',
          // buttonMaxTextLength: 25,   // This will prevent adding text longer than 25 characters
          // minButtons: 0,     // NIJE OBAVEZNO
          maxButtons: 2,    // NIJE OBAVEZNO
          returnKey: 'responseButtons',
          enter: 'Add',
        },
        dividerFull: {
          type: 'dividerFull',
        },
        message2: {
          type: 'textarea',
          title: 'If the response is positive send a message',
          placeholder: 'Thanks! Our support team will contact you shortly to assist you further.',
          value: 'Thanks! Our support team will contact you shortly to assist you further.',
          validators: 'required',
        },
        actionsTest: {
          value: [
            { value: 2 },
          ],
          type: 'action-buttons',
          options: [
            { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
            { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
            { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
          ],
          addActionIcon: SBIconList.PLUS,
          addActionText: 'Add action',
          returnKey: 'responseActions',
        },
        divider: {
          type: 'divider',
        },
        question: {
          type: 'input',
          title: 'If the response is negative',
          value: 'Continue chatting',
          validators: 'required',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'AI will keep chatting with the contact.',
        },
      },
    },
    collect2: {
      showIf: {
        input: 'condition2',
        value: [2],
      },
      type: 'object',
      child: true,
      properties: {
        message: {
          type: 'textarea',
          title: 'Send a message to confirm assignment',
          value: 'I\'d be happy to connect you with our support team. Could you please confirm if you\'d like to proceed?',
          placeholder: 'I\'d be happy to connect you with our support team. Could you please confirm if you\'d like to proceed?',
          validators: 'required|maxLength:1024',
        },
        buttons: {
          child: true,
          value: [
            { title: '👍 Yes', fixed: true },
            { title: 'Continue chat', fixed: true },
          ],
          type: 'message-buttons',
          addButtonIcon: SBIconList.PLUS,
          addButtonText: 'Add button',
          // buttonMaxTextLength: 25,   // This will prevent adding text longer than 25 characters
          // minButtons: 0,     // NIJE OBAVEZNO
          // maxButtons: 10,    // NIJE OBAVEZNO
          returnKey: 'responseButtons',
        },
        dividerFull: {
          type: 'dividerFull',
        },
        message1: {
          type: 'textarea',
          title: 'If the response is positive collect info',
          placeholder: 'Full name, email, and issue description.',
          validators: 'required',
        },
        message2: {
          type: 'textarea',
          title: 'When you\'ve collected info send a message',
          placeholder: 'Thanks! Our support team will contact you shortly to assist you further.',
          value: 'Thanks! Our support team will contact you shortly to assist you further.',
          validators: 'required',
        },
        actionsTest: {
          value: [
            { value: 2 },
            { value: 3 },
          ],
          type: 'action-buttons',
          options: [
            { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
            { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
            { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
          ],
          addActionIcon: SBIconList.PLUS,
          addActionText: 'Add action',
          returnKey: 'responseActions',
        },
        divider: {
          type: 'divider',
        },
        question: {
          type: 'input',
          title: 'If the response is negative',
          value: 'Continue chatting',
          validators: 'required',
          subtitlePrefixIcon: SBIconList.INFO,
          subtitle: 'AI will keep chatting with the contact.',
        },
      },
    },
  },
};

// const pripremaZaBackend = {
//   type: 'object',
//   properties: {
//     condition1: {
//       type: 'dropdown',
//       title: '🧐 If you cannot provide an answer to the contact',
//       validators: 'required',
//       options: [
//         { value: 1, label: 'Assign chat to the support team' },
//         { value: 2, label: 'Collect info and assign chat to the support team' },
//       ],
//     },
//     assign: {
//       showIf: {
//         input: 'condition1',
//         value: [1],
//       },
//       type: 'object',
//       child: true,
//       properties: {
//         message: {
//           type: 'textarea',
//           title: 'Send a message to confirm assignment',
//           placeholder: 'Hmm, I don\'t have the information you\'re looking for. Would you like me to connect you with our support team for further assistance?',
//           value: 'Hmm, I don\'t have the information you\'re looking for. Would you like me to connect you with our support team for further assistance?',
//           validators: 'required|minLength:20|maxLength:1024',
//           warningValidators: 'minLength',
//         },
//         buttons: {
//           child: true,
//           value: [
//             { title: '👍 Yes', fixed: true },
//             { title: 'Continue chat', fixed: true },
//           ],
//           type: 'message-buttons',
//           addButtonIcon: SBIconList.PLUS,
//           addButtonText: 'Add button',
//           minButtons: 0,
//           maxButtons: 2,
//           returnKey: 'responseButtons',
//           enter: 'Add',
//         },
//         dividerFull: {
//           type: 'dividerFull',
//         },
//         positiveResponse: {
//           type: 'textarea',
//           title: 'If the response is positive then send a message',
//           value: 'Thanks! Our support team will contact you shortly to assist you further.',
//           placeholder: 'Thanks! Our support team will contact you shortly to assist you further.',
//           validators: 'required',
//           validatorMessages: {
//             required: 'Message is required.',
//           },
//         },
//         actionsTest: {
//           value: [
//             { value: 2 },
//           ],
//           type: 'action-buttons',
//           options: [
//             { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
//             { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
//             { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
//           ],
//           addActionIcon: SBIconList.PLUS,
//           addActionText: 'Add action',
//           returnKey: 'responseActions',
//         },
//         divider: {
//           type: 'divider',
//         },
//         question: {
//           type: 'input',
//           title: 'If the contact\'s response is negative',
//           value: 'Continue chatting',
//           validators: 'required',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'AI will keep chatting with the contact.',
//         },
//       },
//     },
//     collect: {
//       showIf: {
//         input: 'condition1',
//         value: [2],
//       },
//       type: 'object',
//       child: true,
//       properties: {
//         message: {
//           type: 'textarea',
//           title: 'Send a message to confirm assignment',
//           placeholder: 'Hmm, I don\'t have the information you\'re looking for. Would you like me to connect you with our support team for further assistance?',
//           value: 'Hmm, I don\'t have the information you\'re looking for. Would you like me to connect you with our support team for further assistance?',
//           validators: 'required|minLength:20|maxLength:1024',
//           warningValidators: 'minLength',
//         },
//         buttons: {
//           child: true,
//           value: [
//             { title: '👍 Yes', fixed: true },
//             { title: 'Continue chat', fixed: true },
//           ],
//           type: 'message-buttons',
//           addButtonIcon: SBIconList.PLUS,
//           addButtonText: 'Add button',
//           minButtons: 0,
//           maxButtons: 2,
//           returnKey: 'responseButtons',
//           enter: 'Add',
//         },
//         dividerFull: {
//           type: 'dividerFull',
//         },
//         positiveResponse: {
//           type: 'textarea',
//           title: 'If the response is positive collect info',
//           placeholder: 'Full name, email, and issue description.',
//           validators: 'required',
//           validatorMessages: {
//             required: 'Message is required.',
//           },
//         },
//         positiveResponse2: {
//           type: 'textarea',
//           title: 'When you\'ve collected info send a message',
//           value: 'Thanks! Our support team will contact you shortly to assist you further.',
//           placeholder: 'Thanks! Our support team will contact you shortly to assist you further.',
//           validators: 'required',
//           // validatorMessages: {
//           //   required: 'Message is required.',
//           // },
//         },
//         actionsTest: {
//           value: [
//             { value: 2 },
//             { value: 3 },
//           ],
//           type: 'action-buttons',
//           options: [
//             { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
//             { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
//             { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
//           ],
//           addActionIcon: SBIconList.PLUS,
//           addActionText: 'Add action',
//           returnKey: 'responseActions',
//         },
//         divider: {
//           type: 'divider',
//         },
//         question: {
//           type: 'input',
//           title: 'If the contact\'s response is negative',
//           value: 'Continue chatting',
//           validators: 'required',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'AI will keep chatting with the contact.',
//         },
//       },
//     },
//     condition2: {
//       type: 'dropdown',
//       title: '👥 If the contact requests to speak with the support team',
//       validators: 'required',
//       options: [
//         { value: 1, label: 'Assign chat to the support team' },
//         { value: 2, label: 'Collect info and assign chat to the support team' },
//       ],
//     },
//     requestSupport: {
//       showIf: {
//         input: 'condition2',
//         value: [1],
//       },
//       type: 'object',
//       child: true,
//       properties: {
//         message: {
//           type: 'textarea',
//           title: 'Send a message to confirm assignment',
//           value: 'I\'d be happy to connect you with our support team. Could you please confirm if you\'d like to proceed?',
//           placeholder: 'I\'d be happy to connect you with our support team. Could you please confirm if you\'d like to proceed?',
//           validators: 'required|maxLength:1024',
//         },
//         buttons: {
//           child: true,
//           value: [
//             { title: '👍 Yes', fixed: true },
//             { title: 'Continue chat', fixed: true },
//           ],
//           type: 'message-buttons',
//           addButtonIcon: SBIconList.PLUS,
//           addButtonText: 'Add button',
//           // buttonMaxTextLength: 25,   // This will prevent adding text longer than 25 characters
//           // minButtons: 0,     // NIJE OBAVEZNO
//           maxButtons: 2,    // NIJE OBAVEZNO
//           returnKey: 'responseButtons',
//           enter: 'Add',
//         },
//         dividerFull: {
//           type: 'dividerFull',
//         },
//         message2: {
//           type: 'textarea',
//           title: 'If the response is positive send a message',
//           placeholder: 'Thanks! Our support team will contact you shortly to assist you further.',
//           value: 'Thanks! Our support team will contact you shortly to assist you further.',
//           validators: 'required',
//         },
//         actionsTest: {
//           value: [
//             { value: 2 },
//           ],
//           type: 'action-buttons',
//           options: [
//             { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
//             { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
//             { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
//           ],
//           addActionIcon: SBIconList.PLUS,
//           addActionText: 'Add action',
//           returnKey: 'responseActions',
//         },
//         divider: {
//           type: 'divider',
//         },
//         question: {
//           type: 'input',
//           title: 'If the response is negative',
//           value: 'Continue chatting',
//           validators: 'required',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'AI will keep chatting with the contact.',
//         },
//       },
//     },
//     collect2: {
//       showIf: {
//         input: 'condition2',
//         value: [2],
//       },
//       type: 'object',
//       child: true,
//       properties: {
//         message: {
//           type: 'textarea',
//           title: 'Send a message to confirm assignment',
//           value: 'I\'d be happy to connect you with our support team. Could you please confirm if you\'d like to proceed?',
//           placeholder: 'I\'d be happy to connect you with our support team. Could you please confirm if you\'d like to proceed?',
//           validators: 'required|maxLength:1024',
//         },
//         buttons: {
//           child: true,
//           value: [
//             { title: '👍 Yes 555555', fixed: true },
//             { title: 'Continue chat', fixed: true },
//           ],
//           type: 'message-buttons',
//           addButtonIcon: SBIconList.PLUS,
//           addButtonText: 'Add button',
//           // buttonMaxTextLength: 25,   // This will prevent adding text longer than 25 characters
//           // minButtons: 0,     // NIJE OBAVEZNO
//           // maxButtons: 10,    // NIJE OBAVEZNO
//           returnKey: 'responseButtons',
//         },
//         dividerFull: {
//           type: 'dividerFull',
//         },
//         message1: {
//           type: 'textarea',
//           title: 'If the response is positive collect info',
//           placeholder: 'Full name, email, and issue description.',
//           validators: 'required',
//         },
//         message2: {
//           type: 'textarea',
//           title: 'When you\'ve collected info send a message',
//           placeholder: 'Thanks! Our support team will contact you shortly to assist you further.',
//           value: 'Thanks! Our support team will contact you shortly to assist you further.',
//           validators: 'required',
//         },
//         actionsTest: {
//           value: [
//             { value: 2 },
//             { value: 3 },
//           ],
//           type: 'action-buttons',
//           options: [
//             { value: 1, label: 'Move chat to the Done folder', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will move the chat to the Done folder in your inbox.' },
//             { value: 2, label: 'Unassign chat and mark it as unread', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will unassign the chat and mark it as unread.' },
//             { value: 3, label: 'Send chat summary to the email', type: 'email', subtitlePrefixIcon: SBIconList.INFO, subtitle: 'AI will send the conversation summary and collected info to this email.' },
//           ],
//           addActionIcon: SBIconList.PLUS,
//           addActionText: 'Add action',
//           returnKey: 'responseActions',
//         },
//         divider: {
//           type: 'divider',
//         },
//         question: {
//           type: 'input',
//           title: 'If the response is negative',
//           value: 'Continue chatting',
//           validators: 'required',
//           subtitlePrefixIcon: SBIconList.INFO,
//           subtitle: 'AI will keep chatting with the contact.',
//         },
//       },
//     },
//   },
// };

const image = require('@/assets/img/SB-Icons/two-people.svg'); // this is local image example.

export default {
  name: 'AssistantStep3',
  components: {
    JsonSchema,
  },
  mixins: [ToasterMixin],
  data() {
    return {
      initialized: false,
      steps: 4,
      currentStep: 1,
      initializedSteps: [],
      value: {},
      stepImage: image,
      showShema: false,
      // uiSchema: {
      //   bio: {
      //     'ui:options': {
      //       placeholder: 'Please enter your bio.',
      //       type: 'textarea',
      //       rows: 1,
      //     },
      //   },
      // },
      // uiSchema: {
      //   properties: {
      //     text: {
      //       uiType: 'b-input',
      //     },
      //   },
      // },
      schemaBlueprint: {
        type: 'object',
        properties: {
          test: {
            type: 'input',
            title: 'Test',
            placeholder: 'Test',
            validators: 'required',
            subtitlePrefixIcon: SBIconList.INFO,
            subtitle: 'Test subtitle.',
          },
          typeForm: {
            showIf: {
              input: 'test',
              value: 'aaa',
            },
            type: 'object',
            title: 'Blabla Condition',
            properties: {
              efghij: {
                type: 'input',
                title: 'Test',
                placeholder: 'Test',
                validators: 'required',
                subtitlePrefixIcon: SBIconList.INFO,
                subtitle: 'Test subtitle.',
              },
            },
          },
          type: {
            value: 1,
            type: 'dropdown',
            title: 'Type of AI',
            placeholder: 'Select type of AI',
            validators: 'required',
            subtitlePrefixIcon: SBIconList.INFO,
            subtitle: 'AI in type of {{ option }}.',
            options: [
              { value: 1, label: 'One', color: 'red' },
              { value: 2, label: 'Two', color: 'green' },
              { value: 3, label: 'Three', color: 'blue' },
            ],
            width: 50,
          },
          a: {
            type: 'input',
            title: 'Username',
          },
          b: {
            type: 'object',
            title: 'Blabla',
            properties: {
              e: {
                type: 'input',
                title: 'Is Admin',
              },
              f: {
                type: 'dropdown',
                title: 'Currency format',
                placeholder: 'USD ($100,00.00)',
                options: [
                  { value: 'USD', label: 'USD ($100,00.00)' },
                  { value: 'CAD', label: 'CAD (100,00.00$)' },
                  { value: 'HR', label: 'HR (100,00.00€)' },
                ],
                width: 50,
                subtitlePrefixIcon: SBIconList.INFO,
                subtitle: 'Select the format that AI will use in replies.',
              },
            },
          },
        },
        // if: {
        //   properties: {
        //     test: {
        //       const: 'aaa',
        //     },
        //   },
        // },
        // then: {
        //   properties: {
        //     typeForm: {
        //       show: true,
        //     },
        //   },
        // },
      },
      componentsConfig: [
        {
          componentName: 'BInputWrapper',
          uiSchemaMatcher: { uiType: 'BInputWrapper' },
          // eventName: 'changeInput',
          props: (propName, schema, uiSchema) => {
            // console.log('propName', propName);
            // console.log('schema', schema);
            // console.log('uiSchema', uiSchema);
            return {
              'full-width': true,
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              placeholder: schema.placeholder,
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'SBTextareaWrapper',
          uiSchemaMatcher: { uiType: 'SBTextareaWrapper' },
          // eventName: 'changeInput',
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': true,
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              placeholder: schema.placeholder,
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'SBDropdownWrapper',
          uiSchemaMatcher: { uiType: 'SBDropdownWrapper' },
          // eventName: 'changeInput',
          props: (propName, schema, uiSchema) => {
            // console.log('DROPDOWN');
            // console.log(uiSchema);
            return {
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              placeholder: schema.placeholder,
              validators: schema.validators,
              required: schema.validators ? schema.validators.split('|').includes('required') : false,
              schema,
              // 'ui-schema': uiSchema,
            };
          },
        },
        {
          componentName: 'NMBRButtonsWrapper',
          uiSchemaMatcher: { uiType: 'NMBRButtonsWrapper' },
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': true,
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              placeholder: schema.placeholder,
              validators: schema.validators,
              schema,
            };
          },
        },
        {
          componentName: 'DividerWrapper',
          // uiSchemaMatcher: { uiType: 'DividerWrapper' },
          matcher: { type: 'dividerFull' },
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': true,
              schema,
            };
          },
        },
        {
          componentName: 'DividerWrapper',
          // uiSchemaMatcher: { uiType: 'DividerWrapper' },           // IMPORTANT: maybe uiSchema is not needed
          matcher: { type: 'divider' },
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': false,
              schema,
            };
          },
        },
        {
          componentName: 'SkeletonWrapper',
          // uiSchemaMatcher: { uiType: 'DividerWrapper' },           // IMPORTANT: maybe uiSchema is not needed
          matcher: { type: 'skeleton' },
          props: (propName, schema, uiSchema) => {
            return {
              'full-width': false,
              schema,
            };
          },
        },
        {
          componentName: 'NMBRActionsWrapper',
          matcher: { type: 'action-buttons' },
          props: (propName, schema, uiSchema) => {
            return {
              class: 'sb2-bordered sb2-hoverable sb2-focusable',
              validators: schema.validators,
              schema,
            };
          },
        },
      ],
      wrapperComponentConfig: {
        componentName: 'SBCustomWrapperJSONForms',
        props: (propName, schema, uiSchema) => ({
          title: 'asd' || schema.title || schema.title === '' ? schema.title : propName,
          required: schema.validators ? schema.validators.split('|').includes('required') : false,
          disabled: uiSchema && uiSchema.disabled,
          errorMessage: schema.errorMessage,
          vertical: schema.type === 'object' || schema.type === 'array',
          schema,
        }),
      },
    };
  },
  computed: {
    schema() {
      // console.log('schemaBlueprint PROC', schemaBP);
      // const schema = { ...schemaBP };

      const schema = this.value ? this.filterSchema(JSON.parse(JSON.stringify(schemaBP))) : {};

      // console.log('schema PROC', schema);
      return schema;
      // return {
      //   type: 'object',
      //   properties: {
      //     test: {
      //       type: 'input',
      //       title: 'Test',
      //       placeholder: 'Test',
      //       validators: 'required',
      //       subtitlePrefixIcon: SBIconList.INFO,
      //       subtitle: 'Test subtitle.',
      //     },
      //   },
      // };
    },
    /** Schema needs to be proccesed because some inputs can take less than 100% width and according to the number of elements in a row the appropriate  */
    // proccessedSchema() {
    //   return this.schema;
    // },
    uiSchema() {
      const uiSchema = {
        properties: {
          // b: {
          //   properties: {
          //     c: {
          //       uiType: 'BInputWrapper',
          //     },
          //     d: {
          //       uiType: 'SBDropdownWrapper',
          //     },
          //   },
          // },
        },
      };
      // Object.keys(this.schema.properties).forEach((prop) => {
        // switch (this.schema.properties[prop].type) {
        //   case 'input':
        //     console.log('input');
        //     console.log(prop);
        //     uiSchema.properties[prop] = {
        //       uiType: 'BInputWrapper',
        //     };
        //     break;
        //   case 'textarea':
        //     uiSchema.properties[prop] = {
        //       uiType: 'SBTextareaWrapper',
        //     };
        //     break;
        //   case 'dropdown':
        //     uiSchema.properties[prop] = {
        //       uiType: 'SBDropdownWrapper',
        //     };
        //     break;
        //   case 'message-buttons':
        //     uiSchema.properties[prop] = {
        //       uiType: 'NMBRButtonsWrapper',
        //     };
        //     break;
        //   case 'number':
        //     // uiSchema.properties[prop] = {
        //     //   uiType: 'b-input',
        //     // };
        //     break;
        //   case 'boolean':
        //     // uiSchema.properties[prop] = {
        //     //   uiType: 'b-input',
        //     // };
        //     break;
        //   case 'subform':
        //     uiSchema.properties[prop] = {
              
        //     };
        //     break;
        //   default:
        //     console.log('default');
        //     console.log(prop);
        //     // uiSchema.properties[prop] = {
        //     //   uiType: 'BInputWrapper',
        //     // };
        //     break;
        // }
      // });
      uiSchema.properties = {
        ...uiSchema.properties,
        ...this.proccessUiSchema(this.schema).properties,
      };
      // console.log('uiSchema', uiSchema);
      return uiSchema;
    },
  },
  watch: {
    value: {
      handler() {
        // console.log('value', this.value);
        // console.log('checkData valid', (!this.$refs.jsonSchema.$v.$invalid).toString());
        this.$nextTick(() => {
          this.checkData();
        });
      },
      deep: true,
    },
  },
  updated() {
    this.focusOnInit();
  },
  mounted() {
    setTimeout(() => {
      this.showShema = true;
    }, 5000);


    window.eventBus.$on('assistantStepsInit', this.stepInit);
  },
  methods: {
    stepInit(data) {
      this.initializedSteps.push(data.step);
      if (this.initializedSteps.length === this.steps) {
        this.focusFirstInput();
      }
    },
    focusOnInit: debounce(function f() {
      if (!this.initialized) {
        window.eventBus.$emit('assistantStepsInit', { step: 3 });
        this.initialized = true;
      }
    }, 100),
    focusFirstInput() {
      if (this.currentStep === 3) {
        // Find first input field and focus it
        const input = document.getElementById('assistant-step-3').querySelector('input, .ql-editor');
        if (input) input.focus();
      }
    },
    filterSchema(schema) {
      // console.log('filterSchema', schema);
      // console.log('filterSchema', schema.properties);
      // const filteredSchema = {
      //   ...schema,
      //   properties: {},
      // };
      // const schemaCopy = { ...schema };
      Object.keys(schema.properties).forEach((key) => {
        const prop = schema.properties[key];
        if (prop.showIf) {
          const { input, value } = prop.showIf;
          // console.log('test PROC', this.value[input], value);
          if (
            !this.value[input] ||
            !this.value[input].data ||
            (
              this.value[input].data.value !== value &&
              (!Array.isArray(value) || !value.includes(this.value[input].data.value))
            )
          ) {
            // console.log('DELETE PROC', key);
            delete schema.properties[key];
            delete this.value[key];
          } else {
            // const controlField = this.findFieldInSchema(schemaBP, input);
            // console.log('controlField PROC', this.value[input].data.color);
            schema.properties[key].color = this.value[input].data.color;
          }
        }
        if (schema.properties && schema.properties[key] && prop.type === 'object') {
          schema.properties[key] = this.filterSchema(prop);
        }
      });
      return schema;
    },
    findFieldInSchema(schema, key) {
      if (schema.properties && schema.properties[key]) {
        return schema.properties[key];
      }
      Object.keys(schema.properties).forEach((prop) => {
        if (schema.properties[prop].type === 'object') {
          return this.findFieldInSchema(schema.properties[prop], key);
        }
      });
      return null;
    },
    proccessUiSchema(schema) {
      const uiSchema = {
        properties: {},
      };
      Object.keys(schema.properties).forEach((prop) => {
        switch (schema.properties[prop].type) {
          case 'input':
            // console.log('input');
            // console.log(prop);
            uiSchema.properties[prop] = {
              uiType: 'BInputWrapper',
            };
            break;
          case 'textarea':
            uiSchema.properties[prop] = {
              uiType: 'SBTextareaWrapper',
            };
            break;
          case 'dropdown':
            uiSchema.properties[prop] = {
              uiType: 'SBDropdownWrapper',
            };
            break;
          case 'message-buttons':
            uiSchema.properties[prop] = {
              uiType: 'NMBRButtonsWrapper',
            };
            break;
          // case 'dividerFull':
          //   uiSchema.properties[prop] = {
          //     uiType: 'DividerWrapper',
          //   };
          //   break;
          case 'number':
            // uiSchema.properties[prop] = {
            //   uiType: 'b-input',
            // };
            break;
          case 'boolean':
            // uiSchema.properties[prop] = {
            //   uiType: 'b-input',
            // };
            break;
          case 'object':
            // uiSchema.properties[prop] = {
            uiSchema.properties[prop] = {
              properties: {
                ...this.proccessUiSchema(schema.properties[prop]).properties,
              },
            };
            // };
            break;
          default:
            // console.log('default');
            // console.log(prop);
            // uiSchema.properties[prop] = {
            //   uiType: 'BInputWrapper',
            // };
            break;
        }
      });
      // console.log('returning uiSchema', uiSchema);
      return uiSchema;
    },
    checkFormValid(form) {
      return !Object.keys(form).some((component) => {
        // First check if it has 'Value' key. If it doesn't have it then this is a subform and it needs to be passed to the checkData function recursively.
        if (form[component].valid === undefined) {
          if (!this.checkFormValid(form[component])) {
            return true;
          }
          return false;
        }
        if (form[component] && form[component].valid === false) {
          return true;
        }
        return false;
      });
    },
    checkData() {
      const formValid = this.checkFormValid(this.value);

      this.$emit('input', {
        valid: formValid,
        name: 'assistantStep1',
        data: this.value,
      });
    },
    initDefault(v) {
      this.value = v;
    },
  },
};
</script>

<style scoped>
</style>
